<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
import { formattedCompanyNeedAddress } from "@/helpers/address";
import { toCalendarDayUtcDate } from "@/helpers/date";
import { getContractTypeOptions, getJobExperienceOptions } from "@/helpers/filterOptions";
import { useAuthStore } from "@/modules/auth/store/authStore";
import { clientService } from "@/modules/clients/services/clientService";
import { getAvailabilityOptionsFromCompanyNeed } from "@/modules/companyNeed/helpers/availability";
import { getCompanyNeedPriorityInfos } from "@/modules/companyNeed/helpers/priority";
import companyNeedService from "@/modules/companyNeed/services/companyNeed";
import { CompanyNeedPriority } from "@/modules/companyNeed/types/priority";
import { useConfigurationStore } from "@/store/configurationStore";
import { EContractType } from '@/types/contract';
import { Availabilities } from '@/types/filters';

import type { FetchCompanyAddressResponse } from "@/modules/clients/types";
import type { CompanyNeed, CreateCompanyNeed, UpdateCompanyNeed } from "@/modules/companyNeed/types";
import type { CompanyNeedAddress } from "@/modules/companyNeed/types/address";
import type { ContractType } from "@/modules/companyNeed/types/contract";
import type { LabelValue } from '@/types/common';
import type { ICompanyDB } from '@/types/company';
import type { IHabilitationDocument } from '@/types/documents';
import type { IJobStored, JobExperience } from '@/types/job';
import type { Remuneration } from "@/types/remuneration";

import Alert from "@/dsComponents/Alert/Alert.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import Separator from "@/dsComponents/Separator.vue";

import AvailabilitiesDropdown from "@/components/dropdown/AvailabilitiesDropdown.vue";
import ContractsDropdown from "@/components/dropdown/ContractsDropdown.vue";
import ExperiencesDropdown from "@/components/dropdown/ExperiencesDropdown.vue";
import HabilitationsDropdown from "@/components/dropdown/HabilitationsDropdown.vue";
import JobsDropdown from "@/components/dropdown/JobsDropdown.vue";
import RemunerationDropdown from "@/components/dropdown/RemunerationDropdown.vue";
import PriorityDropdown from "@/modules/companyNeed/components/dropdowns/PriorityDropdown.vue";
import CompanyNeedLocationModal from "@/modules/companyNeed/components/modals/CompanyNeedLocationModal.vue";

const DATE_FORMAT_DISPLAY = "DD MMM, YYYY";

const DEFAULT_REMUNERATION: Remuneration = {
    periodicity: null,
    minimumAmount: null,
    maximumAmount: null,
};

interface ManageCompanyNeedModalProps {
    open: boolean;
    companyId: string;
    companyNeed?: CompanyNeed;
    poolWorkerIds?: string[];
    onCreated?: (companyNeedId: string, payload: CreateCompanyNeed) => void;
    onUpdated?: (companyNeedId: string) => void;
}

interface SubmitOptions {
    action: () => Promise<{ id: string }>;
    successKey: string;
    errorKey: string;
    onSuccess: (id: string) => void;
}

interface CompanyNeedFormState {
    name: string;
    companyId: string;
    companyName: string;
    address: CompanyNeedAddress;
    job: IJobStored | null;
    priority: LabelValue | null;
    contract: LabelValue | null;
    workers: string;
    remuneration: Remuneration;
    experience: LabelValue<JobExperience | null> | null;
    habilitations: IHabilitationDocument[];
    startDate: string | null;
    endDate: string | null;
    availabilities: LabelValue[];
    description: string;
    note: string;
}

const props = defineProps<ManageCompanyNeedModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const configStore = useConfigurationStore();
const authStore = useAuthStore();
const modalStore = useModalStore();
const { toastSuccess, toastError } = useDefaultToast();

const isLoading = ref(false);
const fakeLoading = ref(false);
const fakeLoadingTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const isSubmitted = ref(false);
const isLocationModalOpen = ref(false);
const isAddressDirty = ref(false);
const isAddressWarningOpen = ref(false);
const company = ref<ICompanyDB | null>(null);
const companyAddresses = ref<FetchCompanyAddressResponse[]>([]);

const companyNeedForm = reactive<CompanyNeedFormState>({
    name: "",
    companyId: "",
    companyName: "",
    address: {
        street: "",
        city: "",
        zipCode: "",
        department: "",
        region: "",
        latitude: 0,
        longitude: 0,
    },
    job: null,
    priority: {
        label: t("global.priorities.medium"),
        value: CompanyNeedPriority.MEDIUM,
    },
    contract: {
        label: t(`contractType.${EContractType.INTERIM}`),
        value: EContractType.INTERIM,
    },
    workers: "1",
    remuneration: { ...DEFAULT_REMUNERATION },
    experience: {
        label: t("jobExperience.none"),
        value: null,
    },
    habilitations: [],
    startDate: null,
    endDate: null,
    availabilities: [],
    description: "",
    note: "",
});

const isEditing = computed(() => !!props.companyNeed);

const isLocated = computed(() => !!companyNeedForm.address?.latitude && !!companyNeedForm.address?.longitude);

const isJobInvalid = computed(() => isSubmitted.value && !companyNeedForm.job);
const isAddressInvalid = computed(() => isSubmitted.value && !isEditing.value && !isLocated.value);

const hasMinimumRemuneration = computed(() => (companyNeedForm.remuneration.minimumAmount ?? 0) > 0);
const hasMaximumRemuneration = computed(() => (companyNeedForm.remuneration.maximumAmount ?? 0) > 0);
const hasRemuneration = computed(() => hasMinimumRemuneration.value && hasMaximumRemuneration.value);

const selectedPayFrequencyId = computed(() => {
    if (!hasRemuneration.value) return undefined;
    const payFrequency = configStore.configuration.remunerationPeriodicities.find(
        (periodicity) => periodicity.type === companyNeedForm.remuneration.periodicity
    );
    return payFrequency?.id;
});

const periodPreferencesPayload = computed(() =>
    companyNeedForm.availabilities.map((preference) => preference.value).filter((value) => value !== Availabilities.WEEKEND)
);

const isWeekEndRequired = computed(() => companyNeedForm.availabilities.some((preference) => preference.value === Availabilities.WEEKEND));

const startDateMinDate = computed(() => {
    if (isEditing.value) return undefined;
    return moment().startOf("day").toDate();
});

const endDateMinDate = computed(() => {
    if (!companyNeedForm.startDate) return undefined;
    return moment(companyNeedForm.startDate, DATE_FORMAT_DISPLAY).toDate();
});

const companyAddressLabel = computed(() => {
    if (!companyNeedForm.address) {
        return t("modals.companyNeed.manage.form.placeholders.address");
    }
    return formattedCompanyNeedAddress(companyNeedForm.address);
});

const mapToCompanyNeedAddress = (address: FetchCompanyAddressResponse): CompanyNeedAddress => ({
    street: address.street ?? "",
    city: address.city ?? "",
    zipCode: address.zip ?? "",
    department: address.department ?? "",
    region: address.region ?? "",
    latitude: address.latitude ?? 0,
    longitude: address.longitude ?? 0,
    complement: address.complement ?? undefined,
    country: address.countryCode ?? undefined,
});

const selectedExperience = computed<JobExperience | null>(() => companyNeedForm.experience?.value ?? null);

const resolveCompanyNeedName = (): string => {
    const trimmedName = companyNeedForm.name.trim();
    if (trimmedName) return trimmedName;
    return companyNeedForm.job?.label ?? "";
};

const buildCommonPayload = () => ({
    priority: companyNeedForm.priority?.value as CompanyNeedPriority | undefined,
    startDate: toCalendarDayUtcDate(companyNeedForm.startDate, DATE_FORMAT_DISPLAY),
    endDate: toCalendarDayUtcDate(companyNeedForm.endDate, DATE_FORMAT_DISPLAY),
    contractType: companyNeedForm.contract?.value ? ((companyNeedForm.contract.value as string).toUpperCase() as ContractType) : undefined,
    positions: Number(companyNeedForm.workers),
    description: companyNeedForm.description.trim() || undefined,
    internalNotes: companyNeedForm.note.trim() || undefined,
    habilitations: companyNeedForm.habilitations.map((habilitation) => habilitation.value),
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    salaryMin: hasMinimumRemuneration.value ? companyNeedForm.remuneration.minimumAmount!.toString() : undefined,
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    salaryMax: hasMaximumRemuneration.value ? companyNeedForm.remuneration.maximumAmount!.toString() : undefined,
    payFrequencyId: selectedPayFrequencyId.value,
    periodPreferences: periodPreferencesPayload.value,
    weekEndRequired: isWeekEndRequired.value,
});

const buildCreatePayload = (): CreateCompanyNeed => ({
    ...buildCommonPayload(),
    experience: selectedExperience.value ?? undefined,
    name: resolveCompanyNeedName(),
    address: companyNeedForm.address,
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    jobId: companyNeedForm.job!.value,
    companyId: companyNeedForm.companyId,
    companyName: companyNeedForm.companyName,
    agencyName: authStore.currentAgency?.name ?? "",
    poolWorkerIds: props.poolWorkerIds,
});

const buildUpdatePayload = (): UpdateCompanyNeed => ({
    ...buildCommonPayload(),
    startDate: toCalendarDayUtcDate(companyNeedForm.startDate, DATE_FORMAT_DISPLAY) ?? null,
    endDate: toCalendarDayUtcDate(companyNeedForm.endDate, DATE_FORMAT_DISPLAY) ?? null,
    experience: selectedExperience.value,
    name: resolveCompanyNeedName() || undefined,
    ...(isAddressDirty.value ? { address: companyNeedForm.address } : {}),
});

const fetchCompanyData = async () => {
    isLoading.value = true;
    try {
        company.value = await clientService.fetchCompany(props.companyId);
        companyAddresses.value = await clientService.fetchCompanyAddresses(props.companyId);
    } catch {
        toastError(t("modals.companyNeed.manage.errors.company"));
        modalStore.actions.close(ModalName.ManageCompanyNeedModal);
    } finally {
        isLoading.value = false;
    }
};

const populateFormFromCompanyNeed = () => {
    if (!props.companyNeed) return;

    const currentCompanyNeed = props.companyNeed;

    companyNeedForm.name = currentCompanyNeed.name;
    companyNeedForm.companyName = currentCompanyNeed.companyName;
    companyNeedForm.job = configStore.getJobOption(currentCompanyNeed.jobId) || null;
    companyNeedForm.address = currentCompanyNeed.address;
    companyNeedForm.workers = String(currentCompanyNeed.positionsNumber);
    companyNeedForm.remuneration = currentCompanyNeed.remuneration ?? { ...DEFAULT_REMUNERATION };
    companyNeedForm.habilitations = currentCompanyNeed.habilitations || [];
    companyNeedForm.startDate = currentCompanyNeed.startDate ? moment(currentCompanyNeed.startDate).format(DATE_FORMAT_DISPLAY) : null;
    companyNeedForm.endDate = currentCompanyNeed.endDate ? moment(currentCompanyNeed.endDate).format(DATE_FORMAT_DISPLAY) : null;
    companyNeedForm.description = currentCompanyNeed.description ?? "";
    companyNeedForm.note = currentCompanyNeed.internalNotes ?? "";

    companyNeedForm.availabilities = getAvailabilityOptionsFromCompanyNeed(currentCompanyNeed);

    if (currentCompanyNeed.contractType) {
        companyNeedForm.contract = getContractTypeOptions().find((ct) => ct.value === currentCompanyNeed.contractType) ?? null;
    }

    if (currentCompanyNeed.priority) {
        const priorityInfo = getCompanyNeedPriorityInfos(currentCompanyNeed.priority);
        companyNeedForm.priority = {
            label: priorityInfo.label,
            value: priorityInfo.value,
        };
    }

    if (currentCompanyNeed.experienceId) {
        const experienceOption = getJobExperienceOptions().find((exp) => exp.value === currentCompanyNeed.experienceId);
        if (experienceOption) {
            companyNeedForm.experience = experienceOption;
        }
    }
};

const initializeData = async () => {
    companyNeedForm.companyId = props.companyId;
    await fetchCompanyData();

    if (props.companyNeed) {
        populateFormFromCompanyNeed();
    } else {
        companyNeedForm.companyName = company.value?.name || "";
        if (companyAddresses.value.length > 0) {
            companyNeedForm.address = mapToCompanyNeedAddress(companyAddresses.value[0]);
        }
    }
};

const handleAddressSelect = (address: CompanyNeedAddress) => {
    companyNeedForm.address = address;
    isAddressDirty.value = true;
    isLocationModalOpen.value = false;
};

const validateForm = (): boolean => {
    isSubmitted.value = true;

    if (!companyNeedForm.job) {
        toastError(t("modals.companyNeed.manage.errors.requiredFields"));
        return false;
    }

    return true;
};

const executeSubmit = async ({ action, successKey, errorKey, onSuccess }: SubmitOptions) => {
    isLoading.value = true;
    try {
        const response = await action();
        fakeLoading.value = true;
        const successDelay = isEditing.value ? 0 : 1000;
        fakeLoadingTimeout.value = setTimeout(() => {
            fakeLoading.value = false;
            toastSuccess(t(successKey));
            onSuccess(response.id);
        }, successDelay);
    } catch {
        toastError(t(errorKey));
    } finally {
        isLoading.value = false;
    }
};

const handleStartDateChange = (date: string) => {
    if (!date) {
        companyNeedForm.startDate = null;
        companyNeedForm.endDate = null;
        return;
    }

    companyNeedForm.startDate = date;

    const newStart = moment(date, DATE_FORMAT_DISPLAY);
    const currentEnd = companyNeedForm.endDate ? moment(companyNeedForm.endDate, DATE_FORMAT_DISPLAY) : null;

    if (!currentEnd || newStart.isSameOrAfter(currentEnd)) {
        companyNeedForm.endDate = null;
    }
};

const handleEndDateChange = (date: string) => {
    const endDate = moment(date, DATE_FORMAT_DISPLAY);
    const startDate = moment(companyNeedForm.startDate, DATE_FORMAT_DISPLAY);

    if (endDate.isBefore(startDate)) {
        toastError(t("modals.companyNeed.manage.errors.endDateBeforeStartDate"));
        return;
    }
    companyNeedForm.endDate = date;
};

const handleSubmit = () => {
    if (!validateForm()) return;

    if (!isLocated.value) {
        isAddressWarningOpen.value = true;
        return;
    }

    submitForm();
};

const submitForm = () => {
    isAddressWarningOpen.value = false;

    if (isEditing.value) {
        // biome-ignore lint/style/noNonNullAssertion: Init Biome
        const companyNeedId = props.companyNeed!._id;
        return executeSubmit({
            action: () => companyNeedService.updateCompanyNeed(companyNeedId, buildUpdatePayload()),
            successKey: "modals.companyNeed.manage.success.update",
            errorKey: "modals.companyNeed.manage.errors.update",
            onSuccess: (id) => {
                props.onUpdated?.(id);
                modalStore.actions.close(ModalName.ManageCompanyNeedModal);
            },
        });
    }

    const payload = buildCreatePayload();
    return executeSubmit({
        action: () => companyNeedService.createCompanyNeed(payload),
        successKey: "modals.companyNeed.manage.success.create",
        errorKey: "modals.companyNeed.manage.errors.create",
        onSuccess: (id) => {
            props.onCreated?.(id, payload);
            modalStore.actions.close(ModalName.ManageCompanyNeedModal);
        },
    });
};

const handleAddressWarningEditAddress = () => {
    isAddressWarningOpen.value = false;
    isLocationModalOpen.value = true;
};

onBeforeMount(initializeData);

onBeforeUnmount(() => {
    if (fakeLoadingTimeout.value) {
        clearTimeout(fakeLoadingTimeout.value);
    }
});
</script>

<template>
    <Modal :open="open" max-width="720px" @close="emit('close')" disableOverlayClose close>
        <template #header>
            <div class="manage-company-need-modal__title">
                {{ t(isEditing ? "modals.companyNeed.manage.titles.edit" : "modals.companyNeed.manage.titles.create") }}
            </div>
        </template>
        <div class="manage-company-need-modal__content">
            <div class="manage-company-need-modal__content__section">
                <TextInputAlt
                    v-model="companyNeedForm.name"
                    :placeholder="t('modals.companyNeed.manage.form.placeholders.name')"
                    size="md"
                    data-bot="company-need-name"
                    variant="transparent"
                />
            </div>
            <div class="manage-company-need-modal__content__section">
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-company"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.company") }}
                        </span>
                        <div class="required">*</div>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <ComboButton :label="company?.name" disabled full-width data-bot="company-need-company" />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-work"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.job") }}
                        </span>
                        <div class="required">*</div>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <JobsDropdown
                            v-model="companyNeedForm.job"
                            :readonly="!!companyNeed?.jobId"
                            :show-icon="false"
                            :fit-content="false"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.job')"
                            :error="isJobInvalid"
                            data-bot="company-need-job"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-map-pin-circle"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.address") }}
                        </span>
                        <div class="required">*</div>
                    </div>
                    <div
                        class="manage-company-need-modal__content__section-item-content manage-company-need-modal__content__section-item-content--address"
                    >
                        <ComboButton
                            :label="companyAddressLabel"
                            :placeholder="!companyNeedForm.address"
                            :readonly="isEditing && isLocated"
                            :error="isAddressInvalid || (!isEditing && !isLocated)"
                            :icon-left-style="isLocated ? 'color: var(--green-base, #00B87C);' : 'color: var(--red-base, #FF3B30);'"
                            :icon-left="!isEditing ? 'world-pin' : undefined"
                            full-width
                            data-bot="company-need-address"
                            @click="isLocationModalOpen = true"
                        />
                        <Alert
                            v-if="!!companyNeedForm.address?.city && !isLocated"
                            variant="error"
                            :message="t('modals.companyNeed.manage.form.warnings.content')"
                        />
                    </div>
                </div>
            </div>
            <div class="manage-company-need-modal__content__section-divider">
                <div class="manage-company-need-modal__content__section-divider-label typo-xm-medium">
                    {{ t("modals.companyNeed.manage.form.moreInformations") }}
                </div>

                <Separator class="manage-company-need-modal__content__section-divider-separator" />
            </div>
            <div class="manage-company-need-modal__content__section">
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-priority-1"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.priority") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <PriorityDropdown
                            v-model="companyNeedForm.priority"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.priority')"
                            :show-icon="false"
                            full-width
                            data-bot="company-need-priority"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-signature"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.contract") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <ContractsDropdown
                            v-model="companyNeedForm.contract"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.contract')"
                            :show-icon="false"
                            :fit-content="false"
                            full-width
                            data-bot="company-need-contract"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-users"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.workers") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <TextInputAlt
                            v-model="companyNeedForm.workers"
                            type="number"
                            size="xs"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.workers')"
                            :min="1"
                            variant="grey"
                            data-bot="company-need-workers"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-money"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.remuneration") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <RemunerationDropdown
                            v-model="companyNeedForm.remuneration"
                            :show-icon="false"
                            full-width
                            data-bot="company-need-remuneration"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-level-3"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.experience") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <ExperiencesDropdown
                            v-model="companyNeedForm.experience"
                            :show-icon="false"
                            :fit-content="false"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.experience')"
                            data-bot="company-need-experience"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-doc-certif"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.habilitations") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <HabilitationsDropdown
                            v-model="companyNeedForm.habilitations"
                            :show-icon="false"
                            :fit-content="false"
                            full-width
                            data-bot="company-need-habilitations"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-cal-arrow-right"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.period") }}
                        </span>
                    </div>
                    <div
                        :class="[
                            'manage-company-need-modal__content__section-item-content',
                            { 'manage-company-need-modal__content__section-item-content--date-range': companyNeedForm.startDate },
                        ]"
                    >
                        <DatePicker
                            :value="companyNeedForm.startDate"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.startDate')"
                            :format="DATE_FORMAT_DISPLAY"
                            :min-date="startDateMinDate"
                            size="sm"
                            variant="ghost"
                            full-width
                            clearable
                            data-bot="company-need-start-date"
                            @date-picked="handleStartDateChange"
                        />
                        <template v-if="companyNeedForm.startDate">
                            <i class="ti ti-arrow-right"></i>
                            <DatePicker
                                :value="companyNeedForm.endDate"
                                :placeholder="t('modals.companyNeed.manage.form.placeholders.endDate')"
                                :format="DATE_FORMAT_DISPLAY"
                                :min-date="endDateMinDate"
                                size="sm"
                                variant="ghost"
                                full-width
                                clearable
                                data-bot="company-need-end-date"
                                @date-picked="handleEndDateChange"
                            />
                        </template>
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-cal-dot"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.availabilities") }}
                        </span>
                    </div>
                    <div class="manage-company-need-modal__content__section-item-content">
                        <AvailabilitiesDropdown
                            v-model="companyNeedForm.availabilities"
                            :show-icon="false"
                            :fit-content="false"
                            full-width
                            data-bot="company-need-availabilities"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-answer-text"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.description") }}
                        </span>
                    </div>
                    <div
                        class="manage-company-need-modal__content__section-item-content manage-company-need-modal__content__section-item-content--full"
                    >
                        <TextareaInput
                            v-model="companyNeedForm.description"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.description')"
                            :rows="1"
                            no-border
                            auto-resize
                            size="sm"
                            data-bot="company-need-description"
                        />
                    </div>
                </div>
                <div class="manage-company-need-modal__content__section-item">
                    <div class="manage-company-need-modal__content__section-item-label">
                        <i class="ti ti-note"></i>
                        <span class="typo-xxs-medium">
                            {{ t("modals.companyNeed.manage.form.labels.note") }}
                        </span>
                    </div>
                    <div
                        class="manage-company-need-modal__content__section-item-content manage-company-need-modal__content__section-item-content--full"
                    >
                        <TextareaInput
                            v-model="companyNeedForm.note"
                            :placeholder="t('modals.companyNeed.manage.form.placeholders.note')"
                            :rows="1"
                            no-border
                            auto-resize
                            size="sm"
                            data-bot="company-need-note"
                        />
                    </div>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="manage-company-need-modal__footer">
                <ButtonAlt :label="t('global.actions.cancel')" type="neutral" data-bot="cancel-company-need" @click="emit('close')" />
                <ButtonAlt
                    :label="t(isEditing ? 'modals.companyNeed.manage.buttons.update' : 'modals.companyNeed.manage.buttons.create')"
                    :data-bot="isEditing ? 'save-company-need' : 'create-company-need'"
                    variant="filled"
                    :loading="isLoading || fakeLoading"
                    @click="handleSubmit"
                />
            </div>
        </template>
    </Modal>

    <CompanyNeedLocationModal
        :open="isLocationModalOpen"
        :company-addresses="companyAddresses"
        :default-address="companyNeedForm.address"
        @close="isLocationModalOpen = false"
        @select="handleAddressSelect"
    />

    <Modal
        :open="isAddressWarningOpen"
        :title="t('modals.companyNeed.manage.form.warnings.title')"
        size="sm"
        position="center"
        width="480px"
        close
        action
        @close="isAddressWarningOpen = false"
    >
        <p class="manage-company-need-modal__warning-content typo-sm-regular">
            {{ t("modals.companyNeed.manage.form.warnings.content") }}
        </p>
        <template #footer>
            <div class="manage-company-need-modal__footer">
                <ButtonAlt
                    :label="t('modals.companyNeed.manage.form.warnings.actions.edit')"
                    variant="filled"
                    @click="handleAddressWarningEditAddress"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.manage-company-need-modal {
    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);

        &__section {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-md, 8px);

            &-item {
                display: flex;
                align-items: flex-start;
                gap: var(--spacing-xs, 4px);

                &-label {
                    display: flex;
                    align-items: center;
                    flex-shrink: 0;
                    gap: var(--spacing-sm, 6px);
                    width: 124px;
                    min-height: 28px;
                    color: var(--text-sub-600, #494e61);

                    .required {
                        color: var(--blue-base, #2051f8);
                    }
                }

                &-content {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs, 4px);
                    min-width: 240px;

                    &--full {
                        flex: 1 0 0;
                        padding: 0 var(--spacing-lg, 12px);
                    }

                    &--date-range {
                        flex-direction: row;
                        align-items: center;
                        gap: var(--spacing-xs, 4px);
                    }

                    &--address {
                        gap: var(--spacing-md, 8px);
                    }
                }
            }

            &-divider {
                display: flex;
                align-items: center;
                padding-top: var(--spacing-md, 8px);
                gap: var(--spacing-lg, 12px);

                &-separator {
                    flex: 1 0 0;
                }
            }
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-lg, 12px);
    }
}
</style>
